import { apiLogin, getCurrentUser } from '@api/authApi';
// import { apiLogout } from '@api/authApi';
import { User } from '@models/user';
import { useMutation, useQuery } from 'api-hooks/hooks';
import { Result } from 'api-hooks/types';
import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

const setToken = (token: string, remember: boolean) =>
  (remember ? localStorage : sessionStorage).setItem('token', token);

const getToken = () =>
  sessionStorage.getItem('token') || localStorage.getItem('token') || undefined;

const removeToken = () => {
  sessionStorage.removeItem('token');
  localStorage.removeItem('token');
};

export interface AuthContextData {
  login: (
    email: string,
    password: string,
    remember: boolean
  ) => Promise<Result<{ access: string; refresh: string }>>;
  logout: () => any;
  user?: User;
  token?: string;
  isAuthenticated: boolean;
  isAuthenticating: boolean;
}

export const AuthContext = React.createContext<AuthContextData>({
  login: () => Promise.reject('AuthContext is not initialized yet.'),
  logout: () => {},
  isAuthenticated: false,
  isAuthenticating: false,
});

export const AuthProvider = ({ children }) => {
  const history = useHistory();
  const { pathname, search } = useLocation();
  const [state, setState] = useState<{
    token?: string;
    isAuthenticating: boolean;
  }>({
    token: getToken(),
    isAuthenticating: false,
  });
  const isAuthenticated = !!state.token;
  const [doLogin] = useMutation(apiLogin);
  // const [doLogout] = useMutation(apiLogout);
  const { data: user, error } = useQuery(getCurrentUser(), {
    skip: !isAuthenticated,
    authToken: state.token,
  });

  const login = async (email: string, password: string, remember: boolean) => {
    setState({ isAuthenticating: true });
    removeToken();

    const res = await doLogin(email, password);
    if (res.data) {
      setToken(remember ? res.data.refresh : res.data.access, remember);
      setState({
        token: res.data['access'],
        isAuthenticating: false,
      });
    } else {
      setState({ isAuthenticating: false });
    }
    return res;
  };

  const logout = () => {
    // doLogout();
    removeToken();
    setState({ isAuthenticating: false });
  };

  // Get token from url
  useEffect(() => {
    if (pathname === '/') {
      const query = new URLSearchParams(search);
      const t = query.get('token');
      if (t) {
        setToken(t, true);
        setState({
          token: t,
          isAuthenticating: false,
        });
      }
    }
  }, [pathname, search]);

  // Redirect to /login when token is invalid
  const status = error?.status;
  useEffect(() => {
    if (status === 401) {
      removeToken();
      setState({ isAuthenticating: false });
      history.push('/login');
    }
  }, [status, history, error]);

  return (
    <AuthContext.Provider
      value={{
        login,
        logout,
        isAuthenticated,
        user,
        ...state,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
